/**
 * PendingStrip (BUILD-19 / TICKET-01a) — the schedule queue's live window on
 * the Board. A collapsible bar under the date header: one pill per lane with
 * its open count; new arrivals pulse the bar red and auto-expand it ONCE
 * (per-user quiet toggle); expanded = lane sections with ticket cards.
 * Replaces ESC's pseudo-tech holding buckets — a parked ticket announces
 * itself. Design: research/schedule-queue-design.md (locked 2026-06-03).
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Bell,
  BellOff,
  ChevronDown,
  ChevronUp,
  Pin,
  Plus,
  X,
  Inbox,
  Package,
  RotateCcw,
  CalendarX,
  CloudRain,
  Route as RouteIcon,
  PhoneMissed,
  EyeOff,
  CalendarPlus,
  ExternalLink,
  Camera,
  Layers,
  ClipboardCheck,
} from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Badge } from "@/components/ui/Badge";
import { OverflowMenu, type MenuEntry } from "@/components/ui/Menu";
import { useEntryFormKeys, useListNav, radioChipProps } from "@/lib/keys";
import { CustomerLink } from "@/components/links";
import { useNavigateWithBack } from "@/lib/navBack";
import { cn } from "@/lib/cn";
import { displayCustomerName } from "@/lib/format";
import { usableCoords } from "@/lib/route";
import { supabase } from "@/lib/supabase";
import type { Dispatch } from "@/lib/types";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useActiveTechs, type ActiveTech } from "./api";
import {
  useQueueLanes,
  usePendingTickets,
  useAddTicket,
  useUpdateTicket,
  useResolveTicket,
  useRecentlyCleared,
  useReopenTicket,
  ticketsByLane,
  ageLabel,
  oncallCallerWords,
  PART_STATE_LABEL,
  SOURCE_LABEL,
  partReady,
  type PendingTicket,
  type NewTicket,
} from "./queue";
import { CreateDispatchModal } from "@/features/customer-360/CreateDispatchModal";

/** FIELD-M0: open a tech's part photo (data plate etc.) from job-media. */
async function openPartPhoto(path: string) {
  const { data } = await supabase.storage.from("job-media").createSignedUrl(path, 3600);
  if (data?.signedUrl) window.open(data.signedUrl, "_blank", "noopener");
}

const LAST_SEEN_KEY = "pegasus.queue.lastSeen";
const QUIET_KEY = "pegasus.queue.quiet";
const PINNED_KEY = "pegasus.queue.pinned";
const HIDDEN_KEY = "pegasus.queue.hidden";

const LANE_ICON: Record<string, typeof Inbox> = {
  incoming: Inbox,
  order_parts: Package,
  return_visit: RotateCcw,
  reschedule: CalendarX,
  weather_hold: CloudRain,
  campaign: RouteIcon,
  missed_call: PhoneMissed, // CALLS-00: missed calls are work items
  sale_visit: ClipboardCheck, // SALE-FINALIZE-01: send the tech back to finalize a remote yes
};

function readLastSeen(): string {
  try {
    return localStorage.getItem(LAST_SEEN_KEY) ?? "1970-01-01T00:00:00Z";
  } catch {
    return "1970-01-01T00:00:00Z";
  }
}

/** Group the day's pending tickets by CUSTOMER (matched custno, else same phone,
 * else the ticket on its own) so one customer's missed-call + unscheduled call
 * STACK into a single card instead of scattering across lanes (Seth 2026-06-24). */
function groupByCustomer(tickets: PendingTicket[]): PendingTicket[][] {
  const m = new Map<string, PendingTicket[]>();
  const order: string[] = [];
  for (const t of tickets) {
    const key = t.custno
      ? `c:${t.custno}`
      : t.phone
        ? `p:${t.phone.replace(/\D/g, "")}`
        : `t:${t.id}`;
    if (!m.has(key)) { m.set(key, []); order.push(key); }
    m.get(key)!.push(t);
  }
  return order.map((k) => m.get(k)!);
}

/**
 * CustomerStack (Seth 2026-06-24) — ONE card per customer on the board's pending
 * strip, listing all their open items (missed call, unscheduled call, part…) as
 * compact rows with the same actions the standalone cards had. Cleaner than a
 * separate card per ticket scattered across type lanes; type still shows per row,
 * and the type filters above decide which rows reach here.
 */
function CustomerStack({ tickets, canResolve, laneLabel }: {
  tickets: PendingTicket[];
  canResolve: boolean;
  laneLabel: (k: string) => string;
}) {
  const resolve = useResolveTicket();
  const update = useUpdateTicket();
  const navigate = useNavigateWithBack();
  const [confirm, setConfirm] = useState<string | null>(null);
  // The ticket whose Schedule window is open — books it right from the board, the
  // same modal as the 360 (Seth 2026-06-24).
  const [sched, setSched] = useState<PendingTicket | null>(null);
  const head = tickets[0];
  const name = displayCustomerName(null, head.customer_name);
  const time = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const daysOld = (iso: string) => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  const rowBtn = "inline-flex h-7 shrink-0 items-center gap-1 rounded-md bg-brand-50 px-2.5 text-xs font-semibold text-brand-700 ring-1 ring-inset ring-brand-100 transition-colors hover:bg-brand-100";

  // DESIGN-CALM-01 Wave 2 (Seth 2026-09-22 "go"): a pending ticket is ONE row —
  // what kind, who and where, what and how old, one action; the rest under "…".
  // The one colored chip on the row is the state that needs someone's move.
  return (
    <>
    <div className="rounded-lg border border-hairline bg-white">
      <ul className="divide-y divide-hairline">
        {tickets.map((t) => {
          const isParts = t.lane_key === "order_parts";
          const confirming = confirm === t.id;
          const old = daysOld(t.created_at);
          const facts = [
            t.source === "oncall" && oncallCallerWords(t.notes) ? `“${oncallCallerWords(t.notes)}” — ${t.summary}` : t.summary,
            isParts && t.part_equipment ? `${[t.part_equipment.mfg, t.part_equipment.model].filter(Boolean).join(" ") || t.part_equipment.eq_type || "unit on file"}${t.part_equipment.serial ? ` · S/N ${t.part_equipment.serial}` : ""}` : null,
            isParts && t.part_po ? `PO ${t.part_po}${t.part_eta ? ` · ETA ${t.part_eta}` : ""}` : null,
            t.target_date ? `target ${t.target_date.slice(5).replace("-", "/")}${t.target_window ? ` · ${t.target_window}` : ""}` : null,
            t.lane_key === "missed_call" ? `${t.phone ?? "unknown number"} · ${time(t.created_at)}` : null,
            `${ageLabel(t.created_at)} · ${SOURCE_LABEL[t.source] ?? t.source}${t.source === "tech_mobile" && t.created_by ? ` — ${t.created_by.split("@")[0]}` : ""}`,
          ].filter(Boolean).join(" · ");

          // the one colored chip
          const state = isParts && t.part_state === "needs_ordering" ? { tone: "warn" as const, text: "needs ordering" }
            : isParts && t.part_state === "backordered" ? { tone: "warn" as const, text: "backordered" }
            : isParts && t.part_state === "ordered" ? { tone: "neutral" as const, text: "on order" }
            : isParts && (t.part_state === "arrived" || t.part_state === "in_stock") ? { tone: "good" as const, text: PART_STATE_LABEL[t.part_state] }
            : t.lane_key === "missed_call" && !t.custno ? { tone: "warn" as const, text: "not in system" }
            : old >= 7 ? { tone: "warn" as const, text: `waiting ${ageLabel(t.created_at)}` }
            : null;

          const orderHref = `/purchase-orders?new=1&ticket=${encodeURIComponent(t.id)}&dispatch=${encodeURIComponent(t.origin_dispatch ?? "")}&custno=${encodeURIComponent(t.custno ?? "")}&locno=${encodeURIComponent(t.locno ?? "")}&part=${encodeURIComponent(t.part_number ?? "")}&desc=${encodeURIComponent(t.summary)}${t.part_equipment ? `&equip=${encodeURIComponent(JSON.stringify(t.part_equipment))}` : ""}`;

          // the one visible action
          let primary: ReactNode = null;
          if (canResolve) {
            if (isParts && (t.part_state === "needs_ordering" || t.part_state === "backordered")) {
              primary = <Link to={orderHref} title="Start the purchase order — when it's created with this dispatch #, this ticket flips to On order automatically" className={rowBtn}>Order (PO)</Link>;
            } else if (isParts && t.part_state === "ordered") {
              primary = <button type="button" onClick={() => update.mutate({ id: t.id, part_state: "arrived" })} className={rowBtn}>Part arrived</button>;
            } else if (t.lane_key === "missed_call" && t.custno) {
              primary = <CustomerLink custno={t.custno} locno={t.locno ?? undefined} title="Pull up this customer's record" className={rowBtn}>Qualify</CustomerLink>;
            } else if (t.custno && t.lane_key !== "missed_call" && (!isParts || partReady(t.part_state))) {
              primary = <button type="button" onClick={() => setSched(t)} title="Schedule this onto the board — assign a tech, day and window" className={rowBtn}><CalendarPlus className="h-3.5 w-3.5" />Schedule</button>;
            }
          }

          const menu: MenuEntry[] = [];
          if (canResolve) {
            if (isParts && t.part_state === "needs_ordering") {
              menu.push({ label: "In stock", title: "We already have it on the shelf — no ordering needed", onSelect: () => update.mutate({ id: t.id, part_state: "in_stock" }) });
              menu.push({ label: "Mark ordered", title: "Ordered outside Pegasus (phone / vendor site) — attaches the PO number so it's trackable", onSelect: () => { const po = window.prompt("Ordered outside Pegasus — PO number to attach (leave blank if none):") ?? ""; update.mutate({ id: t.id, part_state: "ordered", part_po: po.trim() || null }); } });
            }
            if (isParts && t.part_state === "ordered") menu.push({ label: "Backordered", title: "Vendor can't fill it yet — flags the wait honestly", onSelect: () => update.mutate({ id: t.id, part_state: "backordered" }) });
            if (isParts && t.part_state === "backordered") menu.push({ label: "Part arrived", onSelect: () => update.mutate({ id: t.id, part_state: "arrived" }) });
          }
          (t.part_photos ?? []).forEach((p, i) => menu.push({ key: p, label: `Photo ${i + 1}`, icon: <Camera />, title: "Open the tech's photo (data plate / part)", onSelect: () => void openPartPhoto(p) }));
          if (t.custno && t.lane_key !== "missed_call") menu.push({ label: "Open the customer", icon: <ExternalLink />, onSelect: () => navigate(`/customers/${t.custno}${t.locno ? `?loc=${encodeURIComponent(t.locno)}` : ""}`) });
          if (canResolve) {
            if (menu.length > 0) menu.push("divider");
            menu.push({ label: "Dismiss", icon: <X />, danger: true, title: "Remove from the queue — handled or not needed (recoverable for 7 days)", onSelect: () => setConfirm(t.id) });
          }

          return (
            <li key={t.id} className="px-3 py-2">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <Badge size="xs" className="w-24 justify-center">{laneLabel(t.lane_key)}</Badge>
                <div className="min-w-0 flex-1 basis-60">
                  <div className="flex min-w-0 items-baseline gap-1.5 text-[13px]">
                    <span className="truncate font-semibold text-slate-900">
                      {t.custno ? (
                        <CustomerLink custno={t.custno} locno={t.locno ?? undefined} className="hover:text-brand-700">{name}</CustomerLink>
                      ) : name}
                    </span>
                    {head.city && <span className="shrink-0 text-slate-400">· {head.city}</span>}
                  </div>
                  <div className="truncate text-xs text-slate-500" title={facts}>{facts}</div>
                </div>
                {state && <Badge size="xs" tone={state.tone}>{state.text}</Badge>}
                {primary}
                {menu.length > 0 && <OverflowMenu items={menu} label="More actions" />}
              </div>
              {confirming && (
                <div className="mt-1.5 flex items-center gap-2 rounded-md bg-slate-50 px-2.5 py-1.5 text-xs text-slate-700">
                  Remove this from the queue? It stays in "Cleared in the last 7 days".
                  <button type="button" onClick={() => { resolve.mutate({ id: t.id, status: "cancelled" }); setConfirm(null); }} className="rounded-md bg-rose-600 px-2 py-0.5 text-[11px] font-semibold text-white transition-colors hover:bg-rose-700">Dismiss</button>
                  <button type="button" onClick={() => setConfirm(null)} className="text-slate-500 hover:text-slate-700">Keep</button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
    {sched && (
      <CreateDispatchModal
        existingTicket={sched}
        custno={sched.custno}
        locno={sched.locno}
        customerName={sched.customer_name}
        address={sched.address}
        city={sched.city}
        phone={sched.phone}
        onClose={() => setSched(null)}
      />
    )}
    </>
  );
}

export function PendingStrip() {
  const lanes = useQueueLanes().data ?? [];
  const tickets = usePendingTickets().data ?? [];
  const canAdd = useCan("queue.add_ticket");
  const canResolve = useCan("queue.resolve");

  // Pinned = docked open: the lanes render IN-FLOW (the board adjusts) and it
  // stays open across reloads — for dispatchers who want the queue always on.
  const [pinned, setPinned] = useState<boolean>(() => {
    try {
      return localStorage.getItem(PINNED_KEY) === "1";
    } catch {
      return false;
    }
  });
  // Content visibility. Starts open when the queue is pinned (docked) so a pinned
  // strip shows its lanes on load; the "Pending" chevron toggles this in EVERY mode
  // now — pinned or not (Seth 2026-06-24: collapsing stopped working while pinned).
  const [expanded, setExpanded] = useState<boolean>(() => {
    try { return localStorage.getItem(PINNED_KEY) === "1"; } catch { return false; }
  });
  const [quiet, setQuiet] = useState<boolean>(() => {
    try {
      return localStorage.getItem(QUIET_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [addOpen, setAddOpen] = useState(false);
  // Show/hide filters per request type — the lane pills toggle their lane on the
  // board (Seth 2026-06-24). Per-dispatcher, persisted across reloads.
  const [hidden, setHidden] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(HIDDEN_KEY) || "[]") as string[]); } catch { return new Set(); }
  });

  const byLane = useMemo(() => ticketsByLane(tickets), [tickets]);
  const total = tickets.length;

  // New-arrival detection: anything created since this browser last looked.
  const [lastSeen, setLastSeen] = useState<string>(readLastSeen);
  const fresh = useMemo(
    () => tickets.filter((t) => t.created_at > lastSeen).length,
    [tickets, lastSeen],
  );
  const autoExpandedRef = useRef(false);
  useEffect(() => {
    // Pulse + auto-expand ONCE per batch of arrivals (LOCKED), unless quiet.
    if (fresh > 0 && !quiet && !autoExpandedRef.current) {
      autoExpandedRef.current = true;
      setExpanded(true);
    }
    if (fresh === 0) autoExpandedRef.current = false;
  }, [fresh, quiet]);

  const markSeen = () => {
    const now = new Date().toISOString();
    setLastSeen(now);
    try {
      localStorage.setItem(LAST_SEEN_KEY, now);
    } catch {
      // ignore
    }
  };
  const toggleExpanded = () => {
    // Works in every mode now — collapse a pinned (docked) strip too; the pin
    // preference is kept, just the lanes hide/show (Seth 2026-06-24).
    setExpanded((v) => {
      if (!v) markSeen(); // opening it = you saw what's new
      return !v;
    });
  };
  const togglePinned = () => {
    setPinned((v) => {
      const next = !v;
      try {
        localStorage.setItem(PINNED_KEY, next ? "1" : "0");
      } catch {
        // ignore
      }
      if (next) { markSeen(); setExpanded(true); } // pin = dock open, content shown
      else setExpanded(false); // unpin folds it back to the slim bar
      return next;
    });
  };
  const toggleQuiet = () => {
    setQuiet((v) => {
      try {
        localStorage.setItem(QUIET_KEY, v ? "0" : "1");
      } catch {
        // ignore
      }
      return !v;
    });
  };

  const toggleLane = (key: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem(HIDDEN_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
    if (!pinned && !expanded) { setExpanded(true); markSeen(); } // show the filter's effect
  };

  const laneCount = (key: string) => byLane.get(key)?.length ?? 0;
  // Parts needing SOMEONE'S move right now (office or dispatcher) read red.
  const laneActionable = (key: string) =>
    key === "order_parts"
      ? (byLane.get(key) ?? []).filter(
          (t) => t.part_state === "needs_ordering" || partReady(t.part_state),
        ).length
      : laneCount(key);

  return (
    <div className="relative border-b border-hairline bg-white px-6 py-1.5">
      {/* Collapsed bar — always one slim row; the lanes OVERLAY the board on
          expand instead of pushing it down (cockpit needs the vertical room).
          z-30 keeps the bar's buttons (pin / bell / add) clickable ABOVE the
          overlay's click-away backdrop. */}
      <div className="relative z-30 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={toggleExpanded}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 transition-colors hover:text-slate-900"
        >
          {expanded ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
          Pending
          <span
            className={cn(
              "rounded-full px-1.5 py-0.5 text-[11px] font-bold tabular-nums",
              fresh > 0 && !quiet
                ? "bg-red-100 text-red-700"
                : total > 0
                  ? "bg-slate-100 text-slate-700"
                  : "bg-slate-50 text-slate-400",
            )}
          >
            {total}
          </span>
          {fresh > 0 && (
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
            </span>
          )}
        </button>

        {/* DESIGN-CALM-01 Wave 2: only buckets with something in them show as
            chips (a hidden one with tickets shows dashed, so nothing is lost);
            every bucket, empty or hidden, is under "All buckets", one click away. */}
        {lanes.filter((l) => laneCount(l.key) > 0).map((l) => {
          const n = laneCount(l.key);
          const hot = laneActionable(l.key);
          const Icon = LANE_ICON[l.key] ?? Inbox;
          const off = hidden.has(l.key);
          return (
            <button
              key={l.key}
              type="button"
              onClick={() => toggleLane(l.key)}
              title={off ? `Show ${l.label}` : `Hide ${l.label} from the board`}
              aria-pressed={!off}
              className={cn(
                "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors",
                off
                  ? "border-dashed border-slate-200 bg-white text-slate-300 hover:text-slate-500"
                  : hot > 0
                    ? "border-red-200 bg-red-50 font-medium text-red-700 hover:bg-red-100"
                    : "border-hairline bg-white font-medium text-slate-600 hover:bg-slate-50",
              )}
            >
              {off ? <EyeOff className="h-3 w-3" /> : <Icon className="h-3 w-3" />}
              {l.label}
              <span className="tabular-nums">{n}</span>
            </button>
          );
        })}
        {lanes.some((l) => laneCount(l.key) === 0) && (
          <OverflowMenu
            align="start"
            trigger={(open) => (
              <span className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors", open ? "border-slate-300 bg-slate-50 text-slate-700" : "border-hairline bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700")}>
                <Layers className="h-3 w-3" /> All buckets <ChevronDown className="h-3 w-3" />
              </span>
            )}
            items={lanes.map((l) => ({ key: l.key, label: l.label, count: laneCount(l.key), muted: laneCount(l.key) === 0, checked: !hidden.has(l.key), title: hidden.has(l.key) ? `Show ${l.label}` : `Hide ${l.label} from the board`, onSelect: () => toggleLane(l.key) }))}
          />
        )}

        <div className="ml-auto flex items-center gap-1.5">
          {/* BOARD-TICKET-02 (Seth 2026-10-06: "Add a ticket... it's confusing
              and almost like it's old stuff left over" — and he read the pin
              as a MAP control): the button now says what it does (parks, not
              books), and the two mystery icons became one menu with labeled,
              checkable settings. */}
          {canAdd && (
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              title="Park a call in this queue — NOT booked yet. For calls waiting on parts, a callback, a reschedule."
              className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-50"
            >
              <Plus className="h-3 w-3" />
              Park a ticket
            </button>
          )}
          <OverflowMenu
            align="end"
            trigger={(open) => (
              <span
                title="Queue settings"
                className={cn(
                  "inline-flex items-center gap-1 rounded-md border px-1.5 py-1 text-[11px] font-medium transition-colors",
                  open ? "border-slate-300 bg-slate-50 text-slate-700" : "border-hairline bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700",
                )}
              >
                {pinned ? <Pin className="h-3.5 w-3.5" /> : quiet ? <BellOff className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}
                <ChevronDown className="h-3 w-3" />
              </span>
            )}
            items={[
              {
                key: "pin",
                label: "Keep the queue docked open",
                checked: pinned,
                title: pinned ? "The board makes room for the open queue — uncheck to fold it back to this bar" : "Dock the queue open; the board makes room for it",
                onSelect: togglePinned,
              },
              {
                key: "alerts",
                label: "Alert on new tickets (pulse + auto-open)",
                checked: !quiet,
                title: quiet ? "Alerts are off in this browser — check to turn the pulse and auto-open back on" : "Uncheck to stop the pulse and auto-open in this browser",
                onSelect: toggleQuiet,
              },
            ]}
          />
        </div>
      </div>

      {/* Lanes content — IN-FLOW when pinned (the board makes room, Seth's
          ask), an OVERLAY over the board when opened ad-hoc (board never
          moves; click away collapses). */}
      {(() => {
        if (!expanded) return null;
        const visibleTickets = tickets.filter((t) => !hidden.has(t.lane_key));
        const custGroups = groupByCustomer(visibleTickets);
        const lanesContent =
          total === 0 ? (
            <p className="py-2 text-center text-xs text-slate-400">Nothing pending.</p>
          ) : custGroups.length === 0 ? (
            <p className="py-2 text-center text-xs text-slate-400">
              All request types are hidden — tap a pill above to show them.
            </p>
          ) : (
            <div className="space-y-2">
              {custGroups.map((g) => (
                <CustomerStack
                  key={g[0].custno ? `c${g[0].custno}` : g[0].id}
                  tickets={g}
                  canResolve={canResolve}
                  laneLabel={(k) => lanes.find((l) => l.key === k)?.label ?? k}
                />
              ))}
            </div>
          );
        if (pinned) {
          return (
            <div className="mt-1.5 max-h-[40vh] space-y-2 overflow-auto">
              {lanesContent}
              {canResolve && <RecentlyCleared />}
            </div>
          );
        }
        return (
          <>
            <div
              className="fixed inset-0 z-20"
              onClick={() => setExpanded(false)}
              aria-hidden="true"
            />
            <div className="absolute left-0 right-0 top-full z-30 max-h-[45vh] space-y-2 overflow-auto border-b border-hairline bg-white px-6 py-3 shadow-e3">
              {lanesContent}
              {canResolve && <RecentlyCleared />}
            </div>
          </>
        );
      })()}

      {addOpen && <AddTicketModal onClose={() => setAddOpen(false)} />}
    </div>
  );
}

/**
 * RecentlyCleared — the accidental-close safety net (Seth 2026-06-10). A quiet
 * toggle row at the bottom of the expanded strip: last 7 days of cleared
 * tickets, each with "Put it back". Deep history lives in Reports (LISTS-01);
 * this is just the "oops" recovery, in the surface where the oops happened.
 */
function RecentlyCleared() {
  const [open, setOpen] = useState(false);
  const cleared = useRecentlyCleared().data ?? [];
  const lanes = useQueueLanes().data ?? [];
  const reopen = useReopenTicket();
  if (cleared.length === 0) return null;
  const laneLabel = (key: string) =>
    lanes.find((l) => l.key === key)?.label ?? key;
  return (
    <div className="border-t border-hairline pt-1.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-[11px] text-slate-400 transition-colors hover:text-slate-600"
      >
        {open ? (
          <ChevronUp className="h-3 w-3" />
        ) : (
          <ChevronDown className="h-3 w-3" />
        )}
        Cleared in the last 7 days ({cleared.length})
      </button>
      {open && (
        <ul className="mt-1.5 space-y-1">
          {cleared.map((t) => (
            <li
              key={t.id}
              className="flex items-center gap-2 rounded-md bg-slate-50 px-2 py-1 text-[11px] text-slate-600"
            >
              <span className="shrink-0 rounded bg-white px-1.5 py-0.5 font-semibold text-slate-500">
                {laneLabel(t.lane_key)}
              </span>
              <span className="min-w-0 flex-1 truncate">
                {t.custno ? (
                  <CustomerLink
                    custno={t.custno}
                    locno={t.locno ?? undefined}
                    title="Pull up this customer's record"
                    className="text-[11px] font-medium text-brand-700 hover:underline"
                  >
                    {displayCustomerName(null, t.customer_name)}
                  </CustomerLink>
                ) : (
                  <span className="font-medium">
                    {displayCustomerName(null, t.customer_name)}
                  </span>
                )}{" "}
                · {t.summary}
              </span>
              <span className="shrink-0 text-slate-400">
                {t.status === "cancelled" ? "cancelled" : "cleared"}
                {t.resolved_by ? ` by ${t.resolved_by.split("@")[0]}` : ""}
                {t.resolved_at ? ` · ${ageLabel(t.resolved_at)}` : ""}
              </span>
              <button
                type="button"
                onClick={() => reopen.mutate(t.id)}
                disabled={reopen.isPending}
                title="Reopen this ticket — it goes back to its lane like it was never cleared"
                className="shrink-0 rounded-md border border-hairline bg-white px-2 py-0.5 font-medium text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-50"
              >
                Put it back
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Quick ticket FROM a dispatch (TICKET-01b) — "Needs part" / "Needs return
 * visit" on a board card. Born linked: customer, location, coordinates, the
 * originating dispatch, and the diagnosing tech (same-tech-goes-back rule)
 * all carry over; the dispatcher only types WHAT's needed.
 */
export function FromDispatchTicketModal({
  dispatch,
  kind,
  onClose,
}: {
  dispatch: Dispatch;
  kind: "order_parts" | "return_visit";
  onClose: () => void;
}) {
  const techs = useActiveTechs("03").data ?? [];
  const add = useAddTicket();
  const [summary, setSummary] = useState("");
  const [notes, setNotes] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [affinity, setAffinity] = useState(dispatch.tech_empno ?? "");
  // Parts detail (TICKET-02) — what the warehouse needs to order RIGHT, and
  // what the dispatcher needs to book the return (install time, money status).
  const [partNumber, setPartNumber] = useState("");
  const [installMin, setInstallMin] = useState("");
  const [quoted, setQuoted] = useState("");
  const [payment, setPayment] = useState<"prepaid" | "cod" | "">("");

  // Esc / outside-click / focus trap come from the shared Radix Modal.
  const customer = displayCustomerName(
    dispatch.locname,
    dispatch.customer_fullname,
  );
  const techName = (t: ActiveTech) =>
    t.empname ?? (`${t.firstname ?? ""} ${t.lastname ?? ""}`.trim() || t.empno);
  const isParts = kind === "order_parts";

  const save = () =>
    add.mutate(
      {
        lane_key: kind,
        custno: dispatch.custno,
        locno: dispatch.locno,
        customer_name: customer || dispatch.custno,
        address: dispatch.service_address ?? null,
        city: dispatch.city ?? null,
        lat: usableCoords(dispatch.loc_latitude, dispatch.loc_longitude)
          ? dispatch.loc_latitude
          : null,
        lng: usableCoords(dispatch.loc_latitude, dispatch.loc_longitude)
          ? dispatch.loc_longitude
          : null,
        phone: dispatch.customer_phone ?? null,
        summary: summary.trim(),
        notes: notes.trim() || null,
        target_date: targetDate || null,
        affinity_empno: affinity || null,
        origin_dispatch: dispatch.dispatch,
        source: "from_dispatch",
        part_number: isParts ? partNumber.trim() || null : null,
        est_install_minutes:
          isParts && installMin ? parseInt(installMin, 10) || null : null,
        quoted_amount: isParts && quoted ? parseFloat(quoted) || null : null,
        payment_status: isParts && payment ? payment : null,
      },
      { onSuccess: onClose },
    );

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={`${isParts ? "Part ticket" : "Return visit"} — ${customer}`}
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-hairline bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!summary.trim() || add.isPending}
            className="rounded-md bg-brand-700 px-3.5 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:bg-brand-300"
          >
            {add.isPending
              ? "Adding…"
              : isParts
                ? "Add part ticket"
                : "Add return visit"}
          </button>
        </div>
      }
    >
        <div className="space-y-4">
          <p className="text-[11px] text-slate-500">
            {[dispatch.service_address, dispatch.city]
              .filter(Boolean)
              .join(" · ")}{" "}
            · from dispatch #{dispatch.dispatch}
          </p>

          <label className="block text-xs text-slate-600">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              {isParts ? "What part is needed?" : "Why does someone go back?"}
            </span>
            <input
              type="text"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              autoFocus
              placeholder={
                isParts
                  ? "Inducer motor · Lennox SLP98V"
                  : "Recheck after part install / finish repair"
              }
              className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
            />
          </label>

          {isParts && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-xs text-slate-600">
                  <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    Part number
                  </span>
                  <input
                    type="text"
                    value={partNumber}
                    onChange={(e) => setPartNumber(e.target.value)}
                    placeholder="if the tech has it"
                    className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
                  />
                </label>
                <label className="block text-xs text-slate-600">
                  <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    Install time (min)
                  </span>
                  <input
                    type="number"
                    min={0}
                    step={15}
                    value={installMin}
                    onChange={(e) => setInstallMin(e.target.value)}
                    placeholder="60"
                    className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm tabular-nums"
                  />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-xs text-slate-600">
                  <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    Quoted ($)
                  </span>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={quoted}
                    onChange={(e) => setQuoted(e.target.value)}
                    placeholder="0"
                    className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm tabular-nums"
                  />
                </label>
                <div className="text-xs text-slate-600">
                  <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    Payment
                  </span>
                  <div className="flex gap-1.5 pt-0.5">
                    {(
                      [
                        ["prepaid", "Prepaid"],
                        ["cod", "COD"],
                        ["", "Not set"],
                      ] as const
                    ).map(([v, label]) => (
                      <button
                        key={label}
                        type="button"
                        onClick={() => setPayment(v)}
                        className={cn(
                          "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                          payment === v
                            ? "border-brand-300 bg-brand-50 text-brand-800"
                            : "border-hairline bg-white text-slate-600 hover:bg-slate-50",
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs text-slate-600">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Tech who goes back
              </span>
              <select
                value={affinity}
                onChange={(e) => setAffinity(e.target.value)}
                className="w-full rounded-md border border-hairline bg-white px-2 py-1.5 text-sm"
              >
                <option value="">— any tech —</option>
                {techs.map((t) => (
                  <option key={t.empno} value={t.empno}>
                    {techName(t)}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-xs text-slate-600">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Target day
              </span>
              <input
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
              />
            </label>
          </div>

          <label className="block text-xs text-slate-600">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Notes
            </span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="optional"
              className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
            />
          </label>
        </div>
    </Modal>
  );
}

interface FoundCustomer {
  custno: string;
  fullname: string | null;
}
interface FoundLocation {
  custno: string;
  locno: string;
  add1: string | null;
  city: string | null;
  zip: string | null;
  phone1: string | null;
  latitude: number | null;
  longitude: number | null;
}

function AddTicketModal({ onClose }: { onClose: () => void }) {
  const lanes = useQueueLanes().data ?? [];
  const techs = useActiveTechs("03").data ?? [];
  const add = useAddTicket();

  const [lane, setLane] = useState("incoming");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<FoundCustomer[]>([]);
  const [picked, setPicked] = useState<FoundCustomer | null>(null);
  const [locations, setLocations] = useState<FoundLocation[]>([]);
  const [loc, setLoc] = useState<FoundLocation | null>(null);
  const [freeName, setFreeName] = useState("");
  const [phone, setPhone] = useState("");
  const [summary, setSummary] = useState("");
  const [notes, setNotes] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [affinity, setAffinity] = useState("");
  // BOARD-TICKET-02: the bridge out — half the confusion was people opening
  // this to BOOK a call. One click hands whatever they typed to the real
  // new-dispatch flow instead.
  const [booking, setBooking] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  // Esc / outside-click / focus trap come from the shared Radix Modal.

  const search = (q: string) => {
    setQuery(q);
    setPicked(null);
    setLoc(null);
    setLocations([]);
    if (timer.current) window.clearTimeout(timer.current);
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    timer.current = window.setTimeout(async () => {
      const { data } = await supabase
        .from("mirror_customer")
        .select("custno, fullname")
        .ilike("fullname", `%${q.trim()}%`)
        .limit(8);
      setResults((data ?? []) as FoundCustomer[]);
    }, 300);
  };

  const pickCustomer = async (c: FoundCustomer) => {
    setPicked(c);
    setResults([]);
    setQuery(displayCustomerName(null, c.fullname) || c.custno);
    const { data } = await supabase
      .from("mirror_location")
      .select("custno, locno, add1, city, zip, phone1, latitude, longitude")
      .eq("custno", c.custno)
      .limit(10);
    const locs = (data ?? []) as FoundLocation[];
    setLocations(locs);
    if (locs.length === 1) {
      setLoc(locs[0]);
      if (locs[0].phone1) setPhone(locs[0].phone1);
    }
  };

  const techName = (t: ActiveTech) =>
    t.empname ?? (`${t.firstname ?? ""} ${t.lastname ?? ""}`.trim() || t.empno);

  const customerName =
    (picked ? displayCustomerName(null, picked.fullname) : "") ||
    freeName.trim() ||
    query.trim();
  const valid = Boolean(customerName) && Boolean(summary.trim());

  // KEYS-01: ↓ ↑ Enter on the customer matches; Enter = next field; Ctrl+Enter = Add.
  const custNav = useListNav({
    count: results.length,
    open: results.length > 0,
    typed: query.trim().length > 0,
    onPick: (i) => { const c = results[i]; if (c) void pickCustomer(c); },
    onClose: () => setResults([]),
  });
  const onFormKeys = useEntryFormKeys(() => { if (valid && !add.isPending) save(); });

  const save = () => {
    const v: NewTicket = {
      lane_key: lane,
      custno: picked?.custno ?? null,
      locno: loc?.locno ?? null,
      customer_name: customerName,
      address: loc?.add1 ?? null,
      city: loc?.city ?? null,
      // Out-of-region geocodes count as un-geocoded (usableCoords, AUDIT A1).
      lat: usableCoords(loc?.latitude ?? null, loc?.longitude ?? null)
        ? (loc?.latitude ?? null)
        : null,
      lng: usableCoords(loc?.latitude ?? null, loc?.longitude ?? null)
        ? (loc?.longitude ?? null)
        : null,
      phone: phone.trim() || null,
      summary: summary.trim(),
      notes: notes.trim() || null,
      target_date: targetDate || null,
      affinity_empno: affinity || null,
      source: "manual",
    };
    add.mutate(v, { onSuccess: onClose });
  };

  if (booking) {
    // Hand off to the real booking flow with whatever they already typed.
    return (
      <CreateDispatchModal
        custno={picked?.custno ?? null}
        locno={loc?.locno ?? null}
        customerName={customerName || "New customer"}
        address={loc?.add1 ?? null}
        city={loc?.city ?? null}
        phone={phone.trim() || null}
        onClose={onClose}
      />
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title="Park a Ticket in the Queue"
      footer={
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setBooking(true)}
            title="Jump to the real booking flow — tech, day, window. Brings along what you typed here."
            className="rounded-md px-2 py-1.5 text-xs font-medium text-brand-700 transition-colors hover:bg-brand-50"
          >
            Booking it now? New dispatch →
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-hairline bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!valid || add.isPending}
              className="rounded-md bg-brand-700 px-3.5 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:bg-brand-300"
            >
              {add.isPending ? "Parking…" : "Park it"}
            </button>
          </div>
        </div>
      }
    >
        <div className="space-y-4" data-entry-form onKeyDown={onFormKeys}>
          {/* BOARD-TICKET-02: say what parking IS before the form asks for
              anything — this was read as "old stuff left over" without it. */}
          <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-500">
            Parking is <span className="font-semibold text-slate-700">not booking</span> — the call sits in a bucket on
            the strip and stays visible until it's booked or resolved. Use it for calls waiting on parts, a
            callback, or a reschedule.
          </p>
          {/* Lane */}
          <div>
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Lane
            </div>
            <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Lane">
              {lanes.map((l) => (
                <button
                  key={l.key}
                  {...radioChipProps(l.key, lane, lanes.map((x) => x.key), setLane)}
                  className={cn(
                    "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                    lane === l.key
                      ? "border-brand-300 bg-brand-50 text-brand-800"
                      : "border-hairline bg-white text-slate-600 hover:bg-slate-50",
                  )}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          {/* Customer */}
          <div className="relative">
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Customer
            </div>
            <input
              type="text"
              value={query}
              data-autofocus=""
              role="combobox" aria-expanded={results.length > 0} aria-autocomplete="list" autoComplete="off"
              onChange={(e) => search(e.target.value)}
              onKeyDown={custNav.onKeyDown}
              placeholder="Search existing customers…"
              className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
            />
            {results.length > 0 && (
              <div role="listbox" data-glass="panel" className="absolute left-0 right-0 z-20 mt-1 overflow-hidden rounded-xl">
                {results.map((c, i) => (
                  <button
                    key={c.custno}
                    type="button" role="option" aria-selected={custNav.active === i} data-index={i} tabIndex={-1}
                    onClick={() => pickCustomer(c)}
                    onMouseEnter={() => custNav.setActive(i)}
                    className={cn("block w-full px-2.5 py-1.5 text-left text-xs transition-colors", custNav.active === i ? "bg-brand-50" : "hover:bg-slate-50")}
                  >
                    <span className="font-medium text-slate-800">
                      {displayCustomerName(null, c.fullname) || c.custno}
                    </span>
                    <span className="ml-1.5 text-slate-400">{c.custno}</span>
                  </button>
                ))}
              </div>
            )}
            {!picked && (
              <input
                type="text"
                value={freeName}
                onChange={(e) => setFreeName(e.target.value)}
                placeholder="…or a NEW customer's name (web/chat callers)"
                className="mt-1.5 w-full rounded-md border border-dashed border-hairline px-2 py-1.5 text-sm"
              />
            )}
            {picked && locations.length > 1 && (
              <div className="mt-1.5 space-y-1">
                {locations.map((l) => (
                  <button
                    key={`${l.custno}|${l.locno}`}
                    type="button"
                    onClick={() => {
                      setLoc(l);
                      if (l.phone1) setPhone(l.phone1);
                    }}
                    className={cn(
                      "block w-full rounded-md border px-2 py-1 text-left text-xs transition-colors",
                      loc?.locno === l.locno
                        ? "border-brand-300 bg-brand-50 text-brand-800"
                        : "border-hairline bg-white text-slate-600 hover:bg-slate-50",
                    )}
                  >
                    {[l.add1, l.city].filter(Boolean).join(" · ") || l.locno}
                  </button>
                ))}
              </div>
            )}
            {picked && loc && (
              <p className="mt-1 text-[11px] text-slate-500">
                {[loc.add1, loc.city].filter(Boolean).join(" · ")}
              </p>
            )}
          </div>

          {/* What */}
          <label className="block text-xs text-slate-600">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              What's needed
            </span>
            <input
              type="text"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder={
                lane === "order_parts"
                  ? "Inducer motor · Lennox SLP98V"
                  : lane === "incoming"
                    ? "Web request: no heat, wants Tuesday AM"
                    : "What is this ticket for?"
              }
              className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs text-slate-600">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Phone
              </span>
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="optional"
                className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
              />
            </label>
            <label className="block text-xs text-slate-600">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Target day
              </span>
              <input
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
              />
            </label>
          </div>

          {(lane === "order_parts" || lane === "return_visit") && (
            <label className="block text-xs text-slate-600">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Tech who should go back
              </span>
              <select
                value={affinity}
                onChange={(e) => setAffinity(e.target.value)}
                className="w-full rounded-md border border-hairline bg-white px-2 py-1.5 text-sm"
              >
                <option value="">— any tech —</option>
                {techs.map((t) => (
                  <option key={t.empno} value={t.empno}>
                    {techName(t)}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="block text-xs text-slate-600">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Notes
            </span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="optional"
              className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
            />
          </label>
        </div>
    </Modal>
  );
}
